import { FrontendFilter } from '../api-models';
import { All } from '../types/enums';
import {
  FilterCategory, FrontendFilterType, FrontendFilterValue, LocalAuthorityWardOptions, SelectedFilters
} from '../types/filters.type';
import { formatSnapshot } from './formatters';
import { DualRangeSlider, IntegerToInteger } from '../shared/utils/forms/constants';

export function cascadeRemoveFilters(
  category: string,
  filter: string,
  selectedFilters: SelectedFilters,
) {
  const categoryWithoutFilter = { ...selectedFilters[category] };
  delete categoryWithoutFilter[filter];

  if (Object.keys(categoryWithoutFilter).length === 0) {
    const restOfFilters = { ...selectedFilters };
    delete restOfFilters[category];
    return restOfFilters;
  }
  return {
    ...selectedFilters,
    [category]: {
      ...categoryWithoutFilter,
    },

  };
}

export const isRangeFilter = (key: string, lookup: Record<string, FrontendFilterType>): boolean => {
  const filterType = lookup[key];
  return filterType === DualRangeSlider || filterType === IntegerToInteger;
};

export default function removeFilterValue(
  selectedFilters: SelectedFilters,
  category: string,
  filter: string,
  filterDisplayName: string,
  filterValue: FrontendFilterValue,
  isRange: boolean
) {
  if (!selectedFilters[category] || !selectedFilters[category][filter]) return selectedFilters;

  if (isRange) {
    return cascadeRemoveFilters(category, filter, selectedFilters);
  }

  const values: FrontendFilterValue[] = selectedFilters[category][filter].selectedValues;
  const updatedValues = values.filter((v: FrontendFilterValue) => !(v.id === filterValue.id && v.displayName === filterValue.displayName));

  if (updatedValues.length === 0) {
    return cascadeRemoveFilters(category, filter, selectedFilters);
  }
  return {
    ...selectedFilters,
    [category]: {
      ...selectedFilters[category],
      [filter]: { displayName: filterDisplayName, selectedValues: updatedValues },
    },
  };
}
export function setFilters(
  category: string,
  filter: string,
  filterDisplayName: string,
  filterValues: FrontendFilterValue[],
  selectedFilters: SelectedFilters,
) {
  if (filterValues.length === 0) {
    return { selectedFilters: cascadeRemoveFilters(category, filter, selectedFilters) };
  }
  return {
    selectedFilters: {
      ...selectedFilters,
      [category]: {
        ...selectedFilters[category],
        [filter]: { displayName: filterDisplayName, selectedValues: filterValues },
      },
    },
  };
}
export function toggleFilter(
  category: string,
  filter: string,
  filterDisplayName: string,
  filterValue: FrontendFilterValue,
  selectedFilters: SelectedFilters,
) {
  const fallbackValues = selectedFilters[category]?.[filter] || { displayName: filterDisplayName, selectedValues: [] };
  const currentValues = fallbackValues.selectedValues || [];
  const updatedValues = currentValues.filter((v: FrontendFilterValue) => v.id !== filterValue.id);
  const valuesExist = currentValues.some((v: FrontendFilterValue) => v.id === filterValue.id);
  const newSelectedValues = valuesExist ? currentValues.filter((v: FrontendFilterValue) => v.id !== filterValue.id) : [...currentValues, filterValue];

  if (valuesExist && updatedValues.length === 0) {
    return { selectedFilters: cascadeRemoveFilters(category, filter, selectedFilters) };
  }

  return {
    selectedFilters: {
      ...selectedFilters,
      [category]: {
        ...selectedFilters[category],
        [filter]: { displayName: filterDisplayName, selectedValues: newSelectedValues },
      },
    },
  };
}
export function filterCount(
  selectedFilters: SelectedFilters,
  filterTypeLookup: Record<string, FrontendFilterType> = {},
): number {
  return Object.values(selectedFilters)
    .flatMap((dropdowns) => Object.entries(dropdowns))
    .reduce((count, [key, filter]) => {
      const valueCount = filter.selectedValues.length;
      const isRange = isRangeFilter(key, filterTypeLookup);
      // range filter values should be counted as 1 because you always select 2 values, but it's just one filter.
      return count + (isRange && valueCount > 0 ? 1 : valueCount);
    }, 0);
}

export function generateSubFilterMenuProps(filterCategories: FilterCategory[]) {
  const subFilterMenuProps = filterCategories.flatMap((category) => {
    const { subcategories } = category;
    return subcategories.flatMap((subcategory) => {
      const { filters } = subcategory;
      return filters.map((filter) => ({
        label: filter.displayName,
        value: filter.displayName,
        category: category.displayName,
      }));
    });
  });
  return subFilterMenuProps;
}

/**
 * A filter is visible if it has no feature_flag, or its feature_flag is active
 * for the current PostHog user. Empty subcategories and categories are pruned.
 */
export function filterCategoriesByFeatureFlags(
  filterCategories: FilterCategory[],
  activeFeatureFlags: string[] | undefined,
): FilterCategory[] {
  const activeSet = new Set(activeFeatureFlags ?? []);
  return filterCategories
    .map((category) => ({
      ...category,
      subcategories: category.subcategories
        .map((subcategory) => ({
          ...subcategory,
          filters: subcategory.filters.filter(
            (filter) => !filter.featureFlag || activeSet.has(filter.featureFlag),
          ),
        }))
        .filter((subcategory) => subcategory.filters.length > 0),
    }))
    .filter((category) => category.subcategories.length > 0);
}

export const adaptBackendFilters = (backendFilters: FrontendFilter[]): SelectedFilters => {
  const adaptedFilters: SelectedFilters = {};

  backendFilters.forEach((filter) => {
    if (!adaptedFilters[filter.category_display_name]) {
      adaptedFilters[filter.category_display_name] = {};
    }

    if (!adaptedFilters[filter.category_display_name][filter.filter_name]) {
      adaptedFilters[filter.category_display_name][filter.filter_name] = {
        displayName: filter.filter_display_name,
        selectedValues: [],
      };
    }

    adaptedFilters[filter.category_display_name][filter.filter_name].selectedValues.push({
      id: filter.filter_value_name,
      displayName: filter.filter_value_display_name,
    });
  });

  return adaptedFilters;
};

export const chipBackgroundColour: Record<string, string> = {
  'In Debt with Council': 'green',
  'Tenancy Type': 'blue',
  'Household Type': 'green',
  Carer: 'purple',
  'Disability Status': 'yellow',
};

export const getSnapshotOptions = (backendResponse: LocalAuthorityWardOptions) => {
  if (!backendResponse) return [];
  return Object.keys(backendResponse).map((snapshot) => ({
    label: formatSnapshot(snapshot, 'long'),
    value: snapshot,
  }));
};
export const getLocalAuthorityOptions = (snapshot: string, backendResponse: LocalAuthorityWardOptions) => {
  const snapshotData = backendResponse?.[snapshot];
  if (!snapshotData) return [];
  return Object.entries(snapshotData)
    .map(([localAuthorityKey, localAuthorityValue]) => ({
      value: localAuthorityKey,
      label: localAuthorityValue.localAuthorityName,
    }));
};

export const getWardOptions = (snapshot: string, localAuthority: string, backendResponse: LocalAuthorityWardOptions) => {
  const localAuthorityData = backendResponse?.[snapshot]?.[localAuthority];
  if (!localAuthorityData) return [];
  return localAuthorityData.wards;
};
export const extendFiltersWithWard = (selectedFilters: SelectedFilters, wardName: string) => {
  if (wardName !== String(All.value) && wardName !== String(All.label)) {
    return {
      ...selectedFilters,
      Demographics: {
        ...selectedFilters.Demographics,
        ward: {
          displayName: 'Ward',
          selectedValues: [{ displayName: wardName, id: wardName }],
        },
      },
    };
  }
  return selectedFilters;
};

export const createFilterTypeLookup = (filters: FilterCategory[]): Record<string, FrontendFilterType> => {
  const lookup: Record<string, FrontendFilterType> = {};
  filters.forEach((category) => {
    category.subcategories.forEach((subcategory) => {
      subcategory.filters.forEach((filter) => {
        lookup[filter.id] = filter.type;
      });
    });
  });
  return lookup;
};

export const concatRangeValues = (values: FrontendFilterValue[], isRange: boolean): FrontendFilterValue[] => {
  if (isRange) {
    if (values.length < 2) throw new Error('Insufficient values for range concatenation');
    return [{
      id: values.map((v) => v.id).join('-'),
      displayName: values.map((v) => v.displayName).join('-'),
    }];
  }
  return values;
};
